import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'
import { CreateShortcodeRequest, UpdateShortcodeRequest, Shortcode, ApiResponse, PaginatedResponse, ShortcodeAnalytics } from './types'

// Determine API base URL from env or window origin
const API_BASE = (import.meta.env.VITE_API_BASE as string) || window.location.origin

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add authentication interceptor
api.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession()
    // For API Gateway Cognito User Pools authorizer, use the ID token
    const idToken = session.tokens?.idToken?.toString()
    if (idToken) {
      config.headers.Authorization = `Bearer ${idToken}`
    }
  } catch (error) {
    console.warn('No authenticated user found')
  }
  return config
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login if unauthorized
      window.location.href = '/login'
    }
    
    // Enhanced error logging for debugging
    console.error('API Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method
    })
    
    return Promise.reject(error)
  }
)

export const createShortcode = async (data: CreateShortcodeRequest): Promise<ApiResponse<{ shortcode: string }>> => {
  try {
    // Use Cognito-protected endpoint
    const response = await api.post('/generate-api', data)
    return {
      success: true,
      data: response.data
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to create shortcode'
    }
  }
}

export const getShortcodes = async (nextToken?: string): Promise<ApiResponse<PaginatedResponse<Shortcode>>> => {
  try {
    const params = nextToken ? { nextToken } : {}
    const response = await api.get('/shortcodes', { params })
    return {
      success: true,
      data: response.data
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to fetch shortcodes'
    }
  }
}

export const updateShortcode = async (shortcode: string, data: UpdateShortcodeRequest): Promise<ApiResponse<Shortcode>> => {
  try {
    const response = await api.put(`/shortcodes/${shortcode}`, data)
    return {
      success: true,
      data: response.data
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to update shortcode'
    }
  }
}

export const deleteShortcode = async (shortcode: string): Promise<ApiResponse<void>> => {
  try {
    await api.delete(`/shortcodes/${shortcode}`)
    return {
      success: true
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to delete shortcode'
    }
  }
}

export const generateQRCode = async (shortcode: string): Promise<ApiResponse<string>> => {
  try {
    const response = await api.get(`/shortcodes/${shortcode}/qr`, {
      responseType: 'arraybuffer'
    })
    
    // Convert PNG bytes to base64 data URL
    const base64 = btoa(
      new Uint8Array(response.data).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ''
      )
    )
    const dataUrl = `data:image/png;base64,${base64}`
    
    return {
      success: true,
      data: dataUrl
    }
  } catch (error: any) {
    console.error('QR Code generation error:', error)
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to generate QR code'
    }
  }
}

export const getShortcodeStats = async (shortcode: string): Promise<ApiResponse<ShortcodeAnalytics>> => {
  try {
    const response = await api.get(`/shortcodes/${shortcode}/stats`)
    return {
      success: true,
      data: response.data
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to fetch statistics'
    }
  }
}
